import { useTranslation } from 'react-i18next';
import { Clock, CheckCircle, XCircle } from 'lucide-react';

interface SlaBadgeProps {
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
  responseBreached?: boolean;
  resolutionBreached?: boolean;
  firstResponseAt?: string | null;
  status?: string;
  compact?: boolean;
}

type SlaState = 'onTime' | 'approaching' | 'breached' | 'responded' | 'closed';

function getSlaState(
  dueAt: string | null | undefined,
  breached: boolean | undefined,
  responded: boolean,
  isClosed: boolean
): SlaState {
  if (isClosed) return 'closed';
  if (responded) return 'responded';
  if (breached) return 'breached';
  if (!dueAt) return 'onTime';

  const now = Date.now();
  const due = new Date(dueAt).getTime();
  const total = due - now;

  if (total <= 0) return 'breached';

  const created = due - now;
  const halfWay = created / 2;
  if (total <= halfWay) return 'approaching';

  return 'onTime';
}

const stateConfig: Record<SlaState, { bg: string; text: string; icon: typeof Clock }> = {
  onTime: { bg: 'bg-success-100', text: 'text-success-700', icon: CheckCircle },
  approaching: { bg: 'bg-warning-100', text: 'text-warning-700', icon: Clock },
  breached: { bg: 'bg-danger-100', text: 'text-danger-700', icon: XCircle },
  responded: { bg: 'bg-info-100', text: 'text-info-700', icon: CheckCircle },
  closed: { bg: 'bg-surface-100', text: 'text-surface-500', icon: CheckCircle },
};

function formatTimeRemaining(dueAt: string): string {
  const diff = new Date(dueAt).getTime() - Date.now();
  if (diff <= 0) return '';
  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 24) return `${Math.floor(hours / 24)}d`;
  if (hours > 0) return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  return `${mins}m`;
}

function SingleSlaBadge({
  label,
  dueAt,
  breached,
  responded,
  isClosed,
  compact,
}: {
  label: string;
  dueAt?: string | null;
  breached?: boolean;
  responded: boolean;
  isClosed: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const state = getSlaState(dueAt, breached, responded, isClosed);
  const cfg = stateConfig[state];
  const Icon = cfg.icon;

  const stateLabel = t(`sla.${state === 'responded' ? 'responded' : state === 'closed' ? 'closed' : state}`);
  const timeLeft = dueAt && state !== 'breached' && state !== 'responded' && state !== 'closed'
    ? formatTimeRemaining(dueAt)
    : '';

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}
        title={`${label}: ${stateLabel}${timeLeft ? ` (${timeLeft})` : ''}`}
      >
        <Icon size={12} />
        {label[0]}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon size={14} />
      {label}: {stateLabel}
      {timeLeft && <span className="opacity-75">({timeLeft})</span>}
    </span>
  );
}

export default function SlaBadge({
  responseDueAt,
  resolutionDueAt,
  responseBreached,
  resolutionBreached,
  firstResponseAt,
  status,
  compact,
}: SlaBadgeProps) {
  const { t } = useTranslation();

  if (!responseDueAt && !resolutionDueAt) return null;

  const isClosed = status === 'closed';
  const hasResponded = !!firstResponseAt;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {responseDueAt && (
        <SingleSlaBadge
          label={t('sla.response')}
          dueAt={responseDueAt}
          breached={responseBreached}
          responded={hasResponded}
          isClosed={isClosed}
          compact={compact}
        />
      )}
      {resolutionDueAt && (
        <SingleSlaBadge
          label={t('sla.resolution')}
          dueAt={resolutionDueAt}
          breached={resolutionBreached}
          responded={false}
          isClosed={isClosed}
          compact={compact}
        />
      )}
    </div>
  );
}
